import { useEffect, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { logger } from '@libs/core';
import { getQuantityModel, MAX_DISPLAY_QTY } from '@libs/shared-ui';

import { useProduct } from '@/entities/product';
import type { FormFields } from '@/entities/purchase-form';

export const useQuantity = () => {
    const { product, tailored, isLoading, isError } = useProduct();
    const { formState, setValue, setError, clearErrors } = useFormContext<FormFields>();
    const fulfilmentMethod = useWatch({
        name: 'fulfilmentMethod',
        compute: (method: string) => method.toUpperCase() as 'BOPIS' | 'SDD' | 'STH',
    });
    const purchaseOption = useWatch<FormFields, 'purchaseOption'>({ name: 'purchaseOption' });
    const quantity = useWatch<FormFields, 'quantity'>({ name: 'quantity' });

    const disabled = formState.isSubmitting || isLoading;

    const quantityModel = useMemo(() => {
        const isMethodAvailable = Boolean(product?.deliveryMethodsList.length);

        return getQuantityModel({
            product,
            tailored,
            sku: product?.sku,
            method: fulfilmentMethod,
            quantity,
            isMethodAvailable,
        });
    }, [fulfilmentMethod, product, quantity, tailored]);

    const { items, minQty, stock, configuredMaxQty, fallback, shouldRender } = quantityModel;

    useEffect(() => {
        if (!product?.sku || configuredMaxQty < MAX_DISPLAY_QTY) return;

        logger.warn(
            `[PRODUCT QUANTITY] SKU ${product.sku} has defaultMax ${configuredMaxQty} which exceeds MAX_DISPLAY_QTY ${MAX_DISPLAY_QTY}`
        );
    }, [configuredMaxQty, product?.sku]);

    useEffect(() => {
        if (isError || !product?.sku) return;

        if (!items.length) {
            setError('quantity', { message: 'No quantity items available' });
            return;
        }

        clearErrors('quantity');
        setValue('quantity', minQty);
    }, [isError, minQty, product?.sku, fulfilmentMethod, stock, purchaseOption]);

    useEffect(() => {
        if (isError || !product?.sku || !items.length) return;

        if (fallback !== quantity) setValue('quantity', fallback);
    }, [fallback, isError, items, minQty, product?.sku, quantity, setValue]);

    return {
        items,
        minQty,
        fulfilmentMethod,
        sku: product?.sku,
        disabled,
        shouldRender: shouldRender && !isError,
    };
};
